import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems, buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
// #257 Belge yaşam döngüsü (Durum) + Onay Durumu — Fatura/İrsaliye ile ortak.
import {
  lifecycleLabel, lifecycleTone, approvalLabel, approvalTone,
  LIFECYCLE_STATUS_OPTIONS, APPROVAL_STATUS_OPTIONS,
} from './invoiceShared';

const API_BASE = '/api/orderheaders';

const STATUS_OPTIONS = [
  { value: 1, label: 'Hazırlanıyor' },
  { value: 2, label: 'Onaylandı' },
  { value: 3, label: 'Sevk Edildi' },
  { value: 4, label: 'Tamamlandı' },
  { value: 5, label: 'İptal' },
];
const statusLabel = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const found = STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : String(value);
};
const statusTone = (value) => {
  switch (Number(value)) {
    case 2:
    case 4:
      return 'ok';
    case 3:
      return 'wait';
    case 5:
      return 'bad';
    default:
      return 'neutral';
  }
};

// Sipariş Türü (Type, Fatura ile aynı enum) — menü konumuna göre filtreli açılır.
// Tür ekranda gösterilmez; her varyant kendi Type değerini otomatik set eder/filtreler.
const ORDER_VARIANTS = {
  sales: {
    baseFilter: 'Type eq 0',
    title: 'Siparişler', subtitle: 'Satış siparişlerini filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Sipariş',
  },
  purchase: {
    baseFilter: 'Type eq 1',
    title: 'Alış Siparişleri', subtitle: 'Alış siparişlerini filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Alış Siparişi',
  },
  // #833 — eski birleşik liste; menüden erişilmez, eski bağlantılar için korunur.
  return: {
    baseFilter: 'Type eq 2 or Type eq 3 or Type eq 4',
    title: 'İade Siparişleri', subtitle: 'İade siparişlerini filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni İade Siparişi',
  },
  returnGood: {
    baseFilter: 'Type eq 2',
    title: 'Müşteriden Sağlam İade', subtitle: 'Müşteriden sağlam iade siparişlerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Sağlam İade Siparişi',
  },
  returnDamaged: {
    baseFilter: 'Type eq 4',
    title: 'Müşteriden Bozuk İade', subtitle: 'Müşteriden bozuk iade siparişlerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Bozuk İade Siparişi',
  },
  returnVendor: {
    baseFilter: 'Type eq 3',
    title: 'Satıcıya İade', subtitle: 'Satıcıya iade siparişlerini filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Satıcıya İade Siparişi',
  },
};

const COLUMN_FILTER_META = {
  orderType: { type: 'number', odataField: 'OrderType' },
  // #257 Operasyonel sipariş durumu (stok/sevkiyat) → "Sipariş Durumu".
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
  // #257 Yaşam döngüsü "Durum" + "Onay Durumu" — Fatura/İrsaliye ile ortak.
  lifecycleStatus: { type: 'select', odataField: 'LifecycleStatus', options: LIFECYCLE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
  approvalStatus: { type: 'select', odataField: 'ApprovalStatus', options: APPROVAL_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
  netAmount: { type: 'number', odataField: 'NetAmount' },
  shippingType: { type: 'text', odataField: 'ShippingType' },
};
// Sipariş başlıklarında string alan az; serbest aramayı string alanlarla sınırla.
const SEARCH_FIELDS = ['OrderDescription', 'ShippingType', 'ShippingAddress'];

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatMoney(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? moneyFormatter.format(parsed) : '-';
}

function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || item.erpId || item.ErpId || '';
  const desc = item.title || item.Title || item.name || item.Name
    || item.shortName || item.ShortName || item.description || item.Description || item.person || item.Person || '';
  return desc ? `${code} - ${desc}`.replace(/^ - /, '') : code;
}

export function OrderHeadersPage({ variant = 'sales' } = {}) {
  const cfg = ORDER_VARIANTS[variant] || ORDER_VARIANTS.sales;
  usePageMeta(cfg.title, cfg.subtitle);
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Kolon etiketleri için küçük lookup'lar (tümü /Select ile yüklenir).
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({});

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: yalnız görünür kolonların OData alanları + kritikler. documentDate kolonunun
  // COLUMN_FILTER_META girdisi yok (filtresiz) ama listede+sıralamada okunur → always'te.
  // customer/representative/vendor/warehouse display kolonları row'dan *Id okur (meta'da yok) → always'te.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'DocumentDate', 'CustomerId', 'RepresentativeId', 'VendorId', 'WarehouseId'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    // Menü konumuna göre Tür filtresi (Satış=0 / Alış=1 / İade=2,3,4).
    baseFilter: cfg.baseFilter,
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/vendors/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/warehouses/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([rep, ven, wh]) => {
      setRepresentatives(unwrapLookupItems(rep));
      setVendors(unwrapLookupItems(ven));
      setWarehouses(unwrapLookupItems(wh));
    }).catch(() => {});
  }, []);

  const representativeName = useMemo(() => {
    const map = new Map(representatives.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [representatives]);
  const vendorName = useMemo(() => {
    const map = new Map(vendors.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [vendors]);
  const warehouseName = useMemo(() => {
    const map = new Map(warehouses.map((g) => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [warehouses]);
  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : '-'), [customerLabels]);

  // Müşteri etiketlerini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = lookupLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || '-'; }); return next; });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);

  useEffect(() => {
    if (rows.length) ensureCustomerLabels(rows.map((r) => r.customerId).filter(Boolean));
  }, [rows, ensureCustomerLabels]);

  // Detay artık tam-sayfa route (/orders/:id). Liste aksiyonları o sayfaya yönlendirir.
  const openNew = () => navigate(`/orders/new?variant=${variant}`);
  const openEdit = (row = selected) => { if (row?.id) navigate(`/orders/${row.id}?edit=1`); };
  const openView = (row = selected) => { if (row?.id) navigate(`/orders/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Sipariş başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('documentDate', {
      header: 'Tarih',
      cell: (info) => displayDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'customer', header: 'Müşteri', enableSorting: false,
      cell: ({ row }) => customerName(row.original.customerId),
    }),
    columnHelper.display({
      id: 'representative', header: 'Temsilci', enableSorting: false,
      cell: ({ row }) => representativeName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'vendor', header: 'Distribütör', enableSorting: false,
      cell: ({ row }) => vendorName(row.original.vendorId),
    }),
    columnHelper.display({
      id: 'warehouse', header: 'Depo', enableSorting: false,
      cell: ({ row }) => warehouseName(row.original.warehouseId),
    }),
    // #257 Yaşam döngüsü (Durum) + Onay Durumu; operasyonel durum ise "Sipariş Durumu" olarak korunur.
    columnHelper.accessor('lifecycleStatus', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : <StatusBadge tone={lifecycleTone(v)}>{lifecycleLabel(v)}</StatusBadge>; },
    }),
    columnHelper.accessor('approvalStatus', {
      header: 'Onay Durumu', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : <StatusBadge tone={approvalTone(v)}>{approvalLabel(v)}</StatusBadge>; },
    }),
    columnHelper.accessor('status', {
      header: 'Sipariş Durumu', meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={statusTone(info.getValue())}>{statusLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.accessor('netAmount', {
      header: 'Net', meta: { filterType: 'number' },
      cell: (info) => <span style={{ display: 'block', textAlign: 'right' }}>{formatMoney(info.getValue())}</span>,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customerName, representativeName, vendorName, warehouseName, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Tarih', valueGetter: ({ row }) => displayDate(row.documentDate) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => customerName(row.customerId) },
        { headerName: 'Temsilci', valueGetter: ({ row }) => representativeName(row.representativeId) },
        { headerName: 'Distribütör', valueGetter: ({ row }) => vendorName(row.vendorId) },
        { headerName: 'Depo', valueGetter: ({ row }) => warehouseName(row.warehouseId) },
        { headerName: 'Durum', valueGetter: ({ row }) => lifecycleLabel(row.lifecycleStatus) },
        { headerName: 'Onay Durumu', valueGetter: ({ row }) => approvalLabel(row.approvalStatus) },
        { headerName: 'Sipariş Durumu', valueGetter: ({ row }) => statusLabel(row.status) },
        { field: 'netAmount', headerName: 'Net Tutar' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `siparisler_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Açıklama veya sevkiyat ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>{cfg.newLabel}</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>Bu siparişi silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default OrderHeadersPage;
